/**
 * Virtuaroo wire protocol — shared between client and Durable Object server.
 *
 * Goals:
 *  - Tiny per-message size (zone broadcasts must fit in DO request budget).
 *  - Easy to extend without breaking older clients.
 *  - All numeric fields use little-endian.
 *
 * Frame layout:
 *   [u8 type][...payload]
 *
 * See SPEC.md §5 (Network protocol) for the throttled-DO rationale.
 */

export const PROTOCOL_VERSION = 1;

/** Tick rate the server broadcasts world snapshots at, in Hz. Lowered to
 *  respect the Cloudflare DO free-tier request budget. Client interpolates
 *  between snapshots so movement still looks smooth. */
export const SERVER_TICK_HZ = 2;
/** Max client → server position updates per second. Throttled identically. */
export const CLIENT_SEND_HZ = 2;
/** Hard cap on players per Durable Object zone. Overflow shards into a parallel DO. */
export const ZONE_PLAYER_CAP = 50;

/** Compact 1-byte enum of avatar animation states. */
export enum AnimState {
  Idle = 0,
  Walking = 1,
  Running = 2,
  Dancing = 3,
  Sitting = 4,
  Waving = 5,
  Headbang = 6,
  Peace = 7,
  Clap = 8,
  HandsUp = 9,
  Lying = 10
}

/** Client → server message types. */
export enum ClientMsgType {
  Hello = 0x01,        // Initial join: nameLen u8, name utf8, hatColor u8
  PosUpdate = 0x02,    // f32 x, f32 y, f32 z, f16 yaw, u8 anim
  Chat = 0x03,         // u8 channel, u8 textLen, text utf8
  Emote = 0x04,        // u8 anim
  Ping = 0x05          // u32 clientTime
}

/** Server → client message types. */
export enum ServerMsgType {
  Welcome = 0x10,      // u16 yourId, u32 serverTime, u8 zoneNameLen, zoneName utf8
  Snapshot = 0x11,     // u16 playerCount, then per-player record (see encodePlayerRecord)
  PlayerLeft = 0x12,   // u16 id
  Chat = 0x13,         // u16 fromId, u8 channel, u8 textLen, text utf8
  ScheduleTick = 0x14, // u32 simTime, u16 stageCount, then per-stage current track id
  Pong = 0x15,         // u32 clientTime, u32 serverTime
  Error = 0x1F         // u8 code, u8 textLen, text utf8
}

export const ChatChannels = {
  Global: 0,
  Zone: 1,
  System: 2
} as const;
export type ChatChannel = (typeof ChatChannels)[keyof typeof ChatChannels];

/** Per-player record inside a Snapshot. 24 bytes + name. */
export interface PlayerRecord {
  id: number;       // u16
  x: number;        // f32
  y: number;        // f32
  z: number;        // f32
  yaw: number;      // f16 (encoded as int16 with 0.001 rad precision)
  anim: AnimState;  // u8
  hatColor: number; // u8
  name: string;     // length-prefixed utf8
}

const YAW_SCALE = 1000; // 0.001 rad precision

/* ------------------------------------------------------------------------- */
/* Encoders                                                                  */
/* ------------------------------------------------------------------------- */

const textEncoder = new TextEncoder();
const textDecoder = new TextDecoder();

export function encodeHello(name: string, hatColor: number): ArrayBuffer {
  const nameBytes = textEncoder.encode(name.slice(0, 24));
  const buf = new ArrayBuffer(1 + 1 + nameBytes.length + 1);
  const view = new DataView(buf);
  view.setUint8(0, ClientMsgType.Hello);
  view.setUint8(1, nameBytes.length);
  new Uint8Array(buf, 2, nameBytes.length).set(nameBytes);
  view.setUint8(2 + nameBytes.length, hatColor & 0xff);
  return buf;
}

export function encodePosUpdate(
  x: number,
  y: number,
  z: number,
  yaw: number,
  anim: AnimState
): ArrayBuffer {
  const buf = new ArrayBuffer(1 + 4 + 4 + 4 + 2 + 1);
  const view = new DataView(buf);
  view.setUint8(0, ClientMsgType.PosUpdate);
  view.setFloat32(1, x, true);
  view.setFloat32(5, y, true);
  view.setFloat32(9, z, true);
  view.setInt16(13, Math.round(yaw * YAW_SCALE), true);
  view.setUint8(15, anim);
  return buf;
}

export function encodeChat(channel: ChatChannel, text: string): ArrayBuffer {
  const textBytes = textEncoder.encode(text.slice(0, 200));
  const buf = new ArrayBuffer(1 + 1 + 1 + textBytes.length);
  const view = new DataView(buf);
  view.setUint8(0, ClientMsgType.Chat);
  view.setUint8(1, channel);
  view.setUint8(2, textBytes.length);
  new Uint8Array(buf, 3, textBytes.length).set(textBytes);
  return buf;
}

export function encodeEmote(anim: AnimState): ArrayBuffer {
  const buf = new ArrayBuffer(2);
  const view = new DataView(buf);
  view.setUint8(0, ClientMsgType.Emote);
  view.setUint8(1, anim);
  return buf;
}

/* ------------------------------------------------------------------------- */
/* Server-side encoders (used by Durable Object)                             */
/* ------------------------------------------------------------------------- */

export function encodeWelcome(yourId: number, serverTime: number, zoneName: string): ArrayBuffer {
  const zoneBytes = textEncoder.encode(zoneName);
  const buf = new ArrayBuffer(1 + 2 + 4 + 1 + zoneBytes.length);
  const view = new DataView(buf);
  view.setUint8(0, ServerMsgType.Welcome);
  view.setUint16(1, yourId, true);
  view.setUint32(3, serverTime, true);
  view.setUint8(7, zoneBytes.length);
  new Uint8Array(buf, 8, zoneBytes.length).set(zoneBytes);
  return buf;
}

export function encodeSnapshot(players: PlayerRecord[]): ArrayBuffer {
  // Compute total size
  let total = 1 + 2; // type + count
  const nameBytesArr: Uint8Array[] = [];
  for (const p of players) {
    const nb = textEncoder.encode(p.name.slice(0, 24));
    nameBytesArr.push(nb);
    total += 2 + 4 + 4 + 4 + 2 + 1 + 1 + 1 + nb.length;
  }
  const buf = new ArrayBuffer(total);
  const view = new DataView(buf);
  let off = 0;
  view.setUint8(off, ServerMsgType.Snapshot); off += 1;
  view.setUint16(off, players.length, true); off += 2;
  for (let i = 0; i < players.length; i++) {
    const p = players[i];
    const nb = nameBytesArr[i];
    view.setUint16(off, p.id, true); off += 2;
    view.setFloat32(off, p.x, true); off += 4;
    view.setFloat32(off, p.y, true); off += 4;
    view.setFloat32(off, p.z, true); off += 4;
    view.setInt16(off, Math.round(p.yaw * YAW_SCALE), true); off += 2;
    view.setUint8(off, p.anim); off += 1;
    view.setUint8(off, p.hatColor & 0xff); off += 1;
    view.setUint8(off, nb.length); off += 1;
    new Uint8Array(buf, off, nb.length).set(nb);
    off += nb.length;
  }
  return buf;
}

export function encodePlayerLeft(id: number): ArrayBuffer {
  const buf = new ArrayBuffer(3);
  const view = new DataView(buf);
  view.setUint8(0, ServerMsgType.PlayerLeft);
  view.setUint16(1, id, true);
  return buf;
}

export function encodeChatBroadcast(fromId: number, channel: ChatChannel, text: string): ArrayBuffer {
  const tb = textEncoder.encode(text.slice(0, 200));
  const buf = new ArrayBuffer(1 + 2 + 1 + 1 + tb.length);
  const view = new DataView(buf);
  view.setUint8(0, ServerMsgType.Chat);
  view.setUint16(1, fromId, true);
  view.setUint8(3, channel);
  view.setUint8(4, tb.length);
  new Uint8Array(buf, 5, tb.length).set(tb);
  return buf;
}

/* ------------------------------------------------------------------------- */
/* Decoders                                                                  */
/* ------------------------------------------------------------------------- */

export interface DecodedSnapshot { players: PlayerRecord[] }

export function decodeServerMessage(data: ArrayBuffer):
  | { type: ServerMsgType.Welcome; yourId: number; serverTime: number; zoneName: string }
  | { type: ServerMsgType.Snapshot; players: PlayerRecord[] }
  | { type: ServerMsgType.PlayerLeft; id: number }
  | { type: ServerMsgType.Chat; fromId: number; channel: ChatChannel; text: string }
  | { type: ServerMsgType.Pong; clientTime: number; serverTime: number }
  | { type: ServerMsgType.Error; code: number; text: string }
  | { type: 'unknown'; raw: number } {
  const view = new DataView(data);
  const t = view.getUint8(0) as ServerMsgType;
  let off = 1;
  switch (t) {
    case ServerMsgType.Welcome: {
      const yourId = view.getUint16(off, true); off += 2;
      const serverTime = view.getUint32(off, true); off += 4;
      const zoneLen = view.getUint8(off); off += 1;
      const zoneName = textDecoder.decode(new Uint8Array(data, off, zoneLen));
      return { type: t, yourId, serverTime, zoneName };
    }
    case ServerMsgType.Snapshot: {
      const count = view.getUint16(off, true); off += 2;
      const players: PlayerRecord[] = [];
      for (let i = 0; i < count; i++) {
        const id = view.getUint16(off, true); off += 2;
        const x = view.getFloat32(off, true); off += 4;
        const y = view.getFloat32(off, true); off += 4;
        const z = view.getFloat32(off, true); off += 4;
        const yawInt = view.getInt16(off, true); off += 2;
        const anim = view.getUint8(off) as AnimState; off += 1;
        const hatColor = view.getUint8(off); off += 1;
        const nameLen = view.getUint8(off); off += 1;
        const name = textDecoder.decode(new Uint8Array(data, off, nameLen));
        off += nameLen;
        players.push({ id, x, y, z, yaw: yawInt / YAW_SCALE, anim, hatColor, name });
      }
      return { type: t, players };
    }
    case ServerMsgType.PlayerLeft: {
      const id = view.getUint16(off, true);
      return { type: t, id };
    }
    case ServerMsgType.Chat: {
      const fromId = view.getUint16(off, true); off += 2;
      const channel = view.getUint8(off) as ChatChannel; off += 1;
      const len = view.getUint8(off); off += 1;
      const text = textDecoder.decode(new Uint8Array(data, off, len));
      return { type: t, fromId, channel, text };
    }
    case ServerMsgType.Pong: {
      const ct = view.getUint32(off, true); off += 4;
      const st = view.getUint32(off, true);
      return { type: t, clientTime: ct, serverTime: st };
    }
    case ServerMsgType.Error: {
      const code = view.getUint8(off); off += 1;
      const len = view.getUint8(off); off += 1;
      const text = textDecoder.decode(new Uint8Array(data, off, len));
      return { type: t, code, text };
    }
    default:
      return { type: 'unknown', raw: t };
  }
}

export function decodeClientMessage(data: ArrayBuffer):
  | { type: ClientMsgType.Hello; name: string; hatColor: number }
  | { type: ClientMsgType.PosUpdate; x: number; y: number; z: number; yaw: number; anim: AnimState }
  | { type: ClientMsgType.Chat; channel: ChatChannel; text: string }
  | { type: ClientMsgType.Emote; anim: AnimState }
  | { type: ClientMsgType.Ping; clientTime: number }
  | { type: 'unknown'; raw: number } {
  const view = new DataView(data);
  const t = view.getUint8(0) as ClientMsgType;
  let off = 1;
  switch (t) {
    case ClientMsgType.Hello: {
      const nameLen = view.getUint8(off); off += 1;
      const name = textDecoder.decode(new Uint8Array(data, off, nameLen));
      off += nameLen;
      const hatColor = view.getUint8(off);
      return { type: t, name, hatColor };
    }
    case ClientMsgType.PosUpdate: {
      const x = view.getFloat32(off, true); off += 4;
      const y = view.getFloat32(off, true); off += 4;
      const z = view.getFloat32(off, true); off += 4;
      const yawInt = view.getInt16(off, true); off += 2;
      const anim = view.getUint8(off) as AnimState;
      return { type: t, x, y, z, yaw: yawInt / YAW_SCALE, anim };
    }
    case ClientMsgType.Chat: {
      const channel = view.getUint8(off) as ChatChannel; off += 1;
      const len = view.getUint8(off); off += 1;
      const text = textDecoder.decode(new Uint8Array(data, off, len));
      return { type: t, channel, text };
    }
    case ClientMsgType.Emote: {
      const anim = view.getUint8(off) as AnimState;
      return { type: t, anim };
    }
    case ClientMsgType.Ping: {
      const ct = view.getUint32(off, true);
      return { type: t, clientTime: ct };
    }
    default:
      return { type: 'unknown', raw: t };
  }
}
